import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  useTheme,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useDispatch, useSelector } from 'react-redux';
import { setDateFilters, clearDateFilters } from '../../../reducers/kanban/ticketActions';
import { getAgentsFromApi } from '../../../actions/agentes';
import { useToken } from '../../../hooks/useToken';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import ClosedTicketsModal from './ClosedTicketsModal';


/**
 * Componente para filtrar tickets por fecha y agente asignado en el tablero Kanban
 * @param {string} boardId - ID del tablero actual para filtrar tickets cerrados
 * @returns {JSX.Element} Componente de filtros de tickets
 */
const KanbanDateFilter = ({ boardId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();

  // Obtener datos del estado de Redux
  const { startDate, endDate, assignedAgent } = useSelector(state => state.kanban.tickets.filters || {});
  const { id_customer } = useSelector(state => state.agent);
  const { agents = { items: [] }, loading: loadingAgents } = useSelector(state => state.agentes || { agents: { items: [] }, loading: false });

  // Estado local para los campos
  const [startDateValue, setStartDateValue] = useState(startDate || '');
  const [endDateValue, setEndDateValue] = useState(endDate || '');
  const [selectedAgent, setSelectedAgent] = useState(assignedAgent || '');
  const [isFiltering, setIsFiltering] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [closedTicketsModalOpen, setClosedTicketsModalOpen] = useState(false);

  // Cargar agentes al inicializar el componente
  useEffect(() => {
    const loadAgents = async () => {
      if ((!agents.items || agents.items.length === 0) && id_customer) {
        try {
          const token = await getTokenSilent();
          if (token) {
            dispatch(getAgentsFromApi(id_customer, token, 0, 50)); // Cargar hasta 50 agentes
          }
        } catch (error) {
          console.error('Error al cargar agentes:', error);
        }
      }
    };

    loadAgents();
  }, [agents.items, id_customer, dispatch, getTokenSilent]);

  // Actualizar el estado local cuando cambian los filtros en Redux
  useEffect(() => {
    setStartDateValue(startDate || '');
    setEndDateValue(endDate || '');
    setSelectedAgent(assignedAgent || '');
    setIsFiltering(Boolean(startDate || endDate || assignedAgent));
  }, [startDate, endDate, assignedAgent]);

  // Manejar cambios en los campos
  const handleStartDateChange = (e) => {
    setStartDateValue(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDateValue(e.target.value);
  };

  const handleAgentChange = (e) => {
    setSelectedAgent(e.target.value);
  };

  // Aplicar filtros
  const handleApplyFilter = () => {
    // Validar que al menos uno de los campos tenga valor
    if (!startDateValue && !endDateValue && !selectedAgent) {
      console.warn('No se ha seleccionado ningún filtro');
      return;
    }

    const filterData = {
      startDate: startDateValue || null,
      endDate: endDateValue || null,
      assignedAgent: selectedAgent || null
    };



    dispatch(setDateFilters(filterData));
    setIsFiltering(true);
  };

  // Limpiar filtros
  const handleClearFilter = async () => {
    setIsClearing(true);
    setStartDateValue('');
    setEndDateValue('');
    setSelectedAgent('');
    
    try {
      // Llamar a clearDateFilters que ahora realiza la recarga automáticamente
      await dispatch(clearDateFilters(getTokenSilent));
      setIsFiltering(false);
    } catch (error) {
      // Aún así marcar como no filtrando para evitar estado inconsistente
      setIsFiltering(false);
    } finally {
      setIsClearing(false);
    }
  };

  // Formatear fecha para mostrar en formato largo (corregido para evitar problemas de zona horaria)
  const formatDateLong = (dateString) => {
    if (!dateString) return '';
    try {
      // Parsear la fecha como local evitando problemas de zona horaria
      const [year, month, day] = dateString.split('-');
      const localDate = new Date(year, month - 1, day);
      return format(localDate, "d 'de' MMMM yyyy", { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  // Obtener el nombre del agente seleccionado
  const getSelectedAgentName = () => {
    if (!selectedAgent) return '';
    const agent = agents.items?.find(a => a._id === selectedAgent);
    return agent ? (agent.email ? agent.email.split('@')[0] : agent.display_name || 'Agente') : selectedAgent;
  };

  // Texto para el tooltip de filtro activo
  const getFilterTooltip = () => {
    const parts = [];
    
    if (startDateValue && endDateValue) {
      if (startDateValue === endDateValue) {
        parts.push(`Fecha: ${formatDateLong(startDateValue)}`);
      } else {
        parts.push(`Fechas: ${formatDateLong(startDateValue)} - ${formatDateLong(endDateValue)}`);
      }
    } else if (startDateValue) {
      parts.push(`Desde: ${formatDateLong(startDateValue)}`);
    } else if (endDateValue) {
      parts.push(`Hasta: ${formatDateLong(endDateValue)}`);
    }

    if (selectedAgent) {
      parts.push(`Agente: ${getSelectedAgentName()}`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'Filtrar tickets';
  };

  // Obtener el conteo de filtros activos
  const getActiveFiltersCount = () => {
    let count = 0;
    if (startDateValue || endDateValue) count++;
    if (selectedAgent) count++;
    return count;
  };

  // Manejar clic en botón de tickets cerrados
  const handleClosedTicketsClick = () => {
    setClosedTicketsModalOpen(true);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1,
        backgroundColor: isFiltering ? theme.palette.primary.lighter : theme.palette.background.paper,
        border: isFiltering ? `1px solid ${theme.palette.primary.main}` : 'none'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterAltIcon sx={{ mr: 0.5, fontSize: 20, color: 'primary.main' }} />
            Filtros de Tickets
            {isFiltering && (
              <Chip
                label={`${getActiveFiltersCount()} filtro${getActiveFiltersCount() > 1 ? 's' : ''} activo${getActiveFiltersCount() > 1 ? 's' : ''}`}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Typography>

          {isFiltering && (
            <Tooltip title="Limpiar todos los filtros">
              <IconButton
                size="small"
                onClick={handleClearFilter}
                color="primary"
                disabled={isClearing}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Filtros */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start', gap: 2 }}>
          {/* Sección de Filtros de Fecha y Agente */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-end', gap: 2, flex: 1 }}>
            {/* Filtros de Fecha */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label="Desde"
                type="date"
                value={startDateValue}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: '160px' }}
              />

              <TextField
                label="Hasta"
                type="date"
                value={endDateValue}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: '160px' }}
              />
            </Box>

            {/* Filtro de Agente */}
            <FormControl size="small" sx={{ minWidth: '200px' }}>
              <InputLabel id="agent-filter-label">Agente Asignado</InputLabel>
              <Select
                labelId="agent-filter-label"
                value={selectedAgent}
                onChange={handleAgentChange}
                label="Agente Asignado"
                disabled={loadingAgents}
              >
                <MenuItem value="">
                  <em>Todos los agentes</em>
                </MenuItem>
                {agents.items?.map((agent) => (
                  <MenuItem key={agent._id} value={agent._id}>
                    {agent.email ? agent.email.split('@')[0] : agent.display_name || 'Agente'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Botón de Aplicar Filtros */}
            <Button
              variant={isFiltering ? "contained" : "outlined"}
              color="primary"
              startIcon={<FilterAltIcon />}
              onClick={handleApplyFilter}
              disabled={!startDateValue && !endDateValue && !selectedAgent}
              size="small"
              sx={{ minWidth: '120px', height: '40px' }}
            >
              Aplicar
            </Button>
          </Box>

          {/* Divisor vertical en desktop */}
          <Box sx={{ display: { xs: 'block', lg: 'block' } }}>
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ 
                display: { xs: 'none', lg: 'block' },
                mx: 1
              }} 
            />
            <Divider 
              orientation="horizontal" 
              sx={{ 
                display: { xs: 'block', lg: 'none' },
                my: 1
              }} 
            />
          </Box>

          {/* Sección de Acciones Especiales */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, gap: 1, alignItems: 'center' }}>
            {/* Botón de Tickets Cerrados */}
            <Tooltip title="Ver tickets cerrados en una vista separada">
              <Button
                variant="outlined"
                startIcon={<ArchiveIcon />}
                onClick={handleClosedTicketsClick}
                size="small"
                sx={{ 
                  minWidth: '140px', 
                  height: '40px',
                  color: '#d32f2f',
                  borderColor: '#d32f2f',
                  backgroundColor: 'transparent',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#ffebee',
                    borderColor: '#b71c1c',
                    color: '#b71c1c',
                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)'
                  },
                  '&:active': {
                    backgroundColor: '#ffcdd2',
                    transform: 'translateY(1px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Tickets Cerrados
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Indicador de filtros activos */}
      {isFiltering && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <FilterAltIcon color="primary" fontSize="small" />
          <Typography variant="caption" color="primary.main" fontWeight="medium">
            {getFilterTooltip()}
          </Typography>
        </Box>
      )}

      {/* Modal de Tickets Cerrados */}
      <ClosedTicketsModal
        open={closedTicketsModalOpen}
        onClose={() => setClosedTicketsModalOpen(false)}
        boardId={boardId}
      />
    </Paper>
  );
};

export default KanbanDateFilter;
